import React, { useEffect, useState } from "react";
import {
  FaTrash,
  FaEdit,
  FaCaretUp,
  FaCaretDown,
  FaSearch,
} from "react-icons/fa";
import { AddOutlined } from "@mui/icons-material";
import CategoryModal from "./CatgoryModal";
import { toast } from "react-toastify";

const API_BASE = "https://mediapi.onlinepharmaque.in/api/v1";

const Category = () => {
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [catImg, setCatImg] = useState(null);

  /* ================= FETCH ALL ================= */
  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_BASE}/getcategory`);
      const data = await res.json();
      setCategories(data.result || []);
    } catch {
      toast.error("Failed to load categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  /* ================= MODAL ================= */
  const toggleModal = () => {
    setShowModal(!showModal);
    setIsEditMode(false);
    setCategoryToEdit(null);
  };

  /* ================= EDIT ================= */
  const editCategory = (id) => {
    const cat = categories.find((c) => c._id === id);
    if (!cat) return;

    setIsEditMode(true);
    setCategoryToEdit(cat);
    setCatImg(cat.categoryImg);
    setShowModal(true);
  };

  /* ================= DELETE ================= */
  const deleteCategory = async (id) => {
    try {
      await fetch(`${API_BASE}/deleteByCatId/${id}`, { method: "DELETE" });
      toast.success("Category deleted");
      setCategories((prev) => prev.filter((c) => c._id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ================= SORT ================= */
  const sortByName = () => {
    const sorted = [...categories].sort((a, b) =>
      sortDir === "asc"
        ? a.MaincategoryName.localeCompare(b.MaincategoryName)
        : b.MaincategoryName.localeCompare(a.MaincategoryName)
    );
    setCategories(sorted);
    setSortDir(sortDir === "asc" ? "desc" : "asc");
  };

  /* ================= SEARCH (API) ================= */
  const handleSearch = async (value) => {
    setSearch(value);

    if (!value.trim()) {
      fetchCategories();
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE}/searchcategory?MaincategoryName=${value}`
      );

      if (!res.ok) {
        setCategories([]);
        return;
      }

      const data = await res.json();
      setCategories(data.result || data);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <>
      <div className="m-6 space-y-6">
        {/* ===== HEADER ===== */}
        <div className="flex justify-between items-center bg-[#1F2A40] p-5 rounded-xl shadow-lg">
          <div>
            <h2 className="text-2xl font-bold text-white">
              Category Management
            </h2>
            <p className="text-sm text-green-400">
              Create, update & manage categories
            </p>
          </div>

          <button
            onClick={toggleModal}
            className="flex items-center gap-2 px-6 py-2 rounded-xl text-white font-semibold hover:scale-105 transition"
            style={{ background: "linear-gradient(135deg,#4cceac,#2bbfa4)" }}
          >
            <AddOutlined />
            Add Category
          </button>
        </div>

        {/* ===== SEARCH ===== */}
        <div className="relative w-80">
          <FaSearch className="absolute top-3 left-3 text-gray-400" />
          <input
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search category..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-[#1F2A40] text-white border border-gray-700 focus:border-green-400 outline-none"
          />
          {loading && (
            <p className="text-xs text-gray-400 mt-1">Searching...</p>
          )}
        </div>

        {/* ===== TABLE ===== */}
        <div className="bg-[#1F2A40] rounded-xl overflow-hidden shadow-lg">
          <table className="w-full text-white text-sm">
            <thead className="bg-gradient-to-r from-[#3E4396] to-[#2c3282]">
              <tr>
                <th className="px-6 py-4 text-left">#</th>
                <th className="px-6 py-4 text-left flex items-center gap-2">
                  Category Name
                  <button onClick={sortByName}>
                    {sortDir === "asc" ? <FaCaretUp /> : <FaCaretDown />}
                  </button>
                </th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {categories.length > 0 ? (
                categories.map((el, i) => (
                  <tr
                    key={el._id}
                    className="border-b border-gray-700 hover:bg-[#2a344d]"
                  >
                    <td className="px-6 py-4">{i + 1}</td>
                    <td className="px-6 py-4 text-green-400 font-medium">
                      {el.MaincategoryName}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-5">
                        <FaEdit
                          className="cursor-pointer text-green-500 hover:scale-125"
                          size={18}
                          onClick={() => editCategory(el._id)}
                        />
                        <FaTrash
                          className="cursor-pointer text-red-500 hover:scale-125"
                          size={18}
                          onClick={() => deleteCategory(el._id)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="text-center py-6 text-gray-400"
                  >
                    No categories found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== MODAL ===== */}
      {showModal && (
        <CategoryModal
          isOpen={showModal}
          toggleModal={toggleModal}
          setCategories={setCategories}
          isEditMode={isEditMode}
          categoryToEdit={categoryToEdit}
          catImg={catImg}
          setCatImg={setCatImg}
        />
      )}
    </>
  );
};

export default Category;
